/**
 * Utility functions for age-related operations
 */
import i18n from "i18next";

/**
 * Translates age in months to Portuguese life stage categories for dogs
 * @param ageInMonths - Age in months
 * @returns Portuguese life stage string
 */
export const getAgeStage = (ageInMonths?: number): string => {
  if (ageInMonths === undefined) {
    return i18n.t("utils:ageStage.unknown");
  } else if (ageInMonths <= 9) {
    return i18n.t("utils:ageStage.puppy");
  } else if (ageInMonths <= 36) {
    // Up to 3 years (36 months)
    return i18n.t("utils:ageStage.young");
  } else if (ageInMonths <= 84) {
    // Up to 7 years (84 months)
    return i18n.t("utils:ageStage.adult");
  } else if (ageInMonths <= 168) {
    // Up to 14 years (168 months)
    return i18n.t("utils:ageStage.senior");
  } else {
    // 15+ years now grouped into 'Sénior' (no special label)
    return i18n.t("utils:ageStage.senior");
  }
};

/**
 * Converts age in months to years and months display
 * @param ageInMonths - Age in months
 * @returns Formatted age string (e.g., "2 anos e 3 meses", "8 meses")
 */
export const formatAge = (ageInMonths: number): string => {
  const years = Math.floor(ageInMonths / 12);
  const months = ageInMonths % 12;

  if (years === 0) {
    return i18n.t("utils:age.months", { count: months });
  } else if (months === 0) {
    return i18n.t("utils:age.years", { count: years });
  } else {
    return i18n.t("utils:age.yearsAndMonths", {
      years: i18n.t("utils:age.years", { count: years }),
      months: i18n.t("utils:age.months", { count: months }),
    });
  }
};

/**
 * Gets both age stage and formatted age
 * @param ageInMonths - Age in months
 * @returns Object with stage and formatted age
 */
export const getAgeInfo = (ageInMonths: number) => ({
  stage: getAgeStage(ageInMonths),
  formatted: formatAge(ageInMonths),
});
